/**
 * Entry requirements page (/conditions), FR + EN: copy of the design canvas "Gravel Games — Site
 * 2027" (Conditions.dc.html), based on the sporting rules V7. Placeholders: {{n}} (age),
 * {{who}} (which team member). Event ids are those of SITE_EVENTS in apps/web/src/lib/eligibility.ts.
 */
type EventId = 'r5' | 'r10' | 'r20' | 'c35' | 'c70' | 'c100' | 'rr12' | 'rr24' | 'e12';
type Disc = 'running' | 'cycling' | 'c35' | 'rr' | 'e12';
interface DocGroup {
  label: string;
  minor: boolean;
  /** `after` continues the sentence after the link. */
  items: { text: string; url?: string; urlLabel?: string; after?: string }[];
}

export interface SiteConditionsContent {
  docTitle: string;
  eyebrow: string;
  title: string;
  intro: string;
  jumpLabel: string;
  jump: { label: string; href: string }[];
  draft: string;
  readRules: string;
  readDetail: string;
  seeEvent: string;
  ev: Record<EventId, string>;
  disc: Record<Disc, string>;
  years: string;
  ages: {
    eyebrow: string;
    title: string;
    sub: string;
    colEvent: string;
    colDisc: string;
    colAge: string;
    colNote: string;
    notes: Record<EventId, string>;
    minor: string;
  };
  docs: {
    eyebrow: string;
    title: string;
    sub: string;
    items: {
      title: string;
      summary: string;
      ref: string;
      open?: boolean;
      reserve?: string;
      groups: DocGroup[];
    }[];
  };
  check: {
    eyebrow: string;
    title: string;
    sub: string;
    q1: string;
    q1ph: string;
    q2: string;
    q2ph: string;
    q3: string;
    lic: string;
    non: string;
    reset: string;
    empty: string;
    okHead: string;
    okAge: string;
    koHead: string;
    koAge: string;
    confirmAge: string;
    altTitle: string;
    none: string;
    docsTitle: string;
    minorTitle: string;
    notesTitle: string;
    ppsLink: string;
    licExpLink: string;
    ffcHealthLink: string;
    ffcHealthUrl: string;
    licExpUrl: string;
    pair: {
      q2: string;
      q3: string;
      you: string;
      mate: string;
      lic: string;
      non: string;
      hint: string;
      okHead: string;
      okAge: string;
      koHead: string;
      koAge: string;
      whoYou: string;
      whoMate: string;
      whoBoth: string;
      altTitle: string;
      docsTitle: string;
    };
    register: string;
    soon: string;
    disclaimer: string;
    noDocs35: string;
    d: {
      runLic: string[];
      runNon: string[];
      runMinor: string[];
      c35Minor: string[];
      /** Timed cycling races: the same three documents, licensed or not. */
      cyc: string[];
      rrLic: string[];
      rrNon: string[];
      rrHealthAdult: string;
      e12: string[];
    };
    m: { consent: string; rrHealth: string };
    n: {
      ppsNote: string;
      noEbike: string;
      untimed: string;
      rrFrame: string;
      rrBike: string;
      e12Team: string;
      e12Rules: string;
      noStart: string;
      id: string;
    };
  };
  rules: {
    eyebrow: string;
    title: string;
    sub: string;
    items: { title: string; ref: string; text: string; dark?: boolean }[];
  };
  ins: {
    eyebrow: string;
    title: string;
    sub: string;
    items: {
      title: string;
      tag: string;
      kind: 'org' | 'rec' | 'you';
      text: string;
      points?: string[];
      foot?: string;
    }[];
    reserve: string;
    link: string;
  };
  cancel: {
    eyebrow: string;
    title: string;
    status: string;
    keyTitle: string;
    faqTitle: string;
    points: string[];
    faq: { q: string; a: string }[];
    link: string;
  };
  full: {
    eyebrow: string;
    title: string;
    sub: string;
    readOnline: string;
    download: string;
    docs: { title: string; lang: string; code: 'fr' | 'en'; file: string; pdf: string }[];
    version: string;
  };
}

export const siteConditions: { fr: SiteConditionsContent; en: SiteConditionsContent } = {
  fr: {
    eyebrow: 'Conditions de participation',
    title: 'Tout savoir avant de prendre le départ',
    intro:
      "Gravel Running, Gravel Cycling, Run & Ride ou Endurance 12H : chaque format a ses propres conditions de participation. Retrouve ici l'essentiel pour choisir ton épreuve, préparer ton inscription et prendre le départ dans les meilleures conditions.",
    jumpLabel: 'Dans cette rubrique',
    jump: [
      {
        label: 'Âge minimum',
        href: '#ages',
      },
      {
        label: 'Documents à fournir',
        href: '#documents',
      },
      {
        label: 'Puis-je participer ?',
        href: '#verifier',
      },
      {
        label: 'Règles essentielles',
        href: '#regles',
      },
      {
        label: 'Assurances',
        href: '#assurances',
      },
      {
        label: 'Annulation',
        href: '#annulation',
      },
      {
        label: 'Règlement complet',
        href: '#reglement',
      },
    ],
    draft:
      "Contenus établis d'après le règlement sportif V7 de l'édition 2027. Cette présentation simplifiée ne remplace pas le règlement intégral, qui fait foi.",
    readRules: 'Lire le règlement complet',
    readDetail: 'Voir le détail',
    seeEvent: "Voir l'épreuve",
    ev: {
      r5: 'Gravel Running 5K',
      r10: 'Gravel Running 10K',
      r20: 'Gravel Running 20K',
      c35: 'Gravel Cycling 35K — Randonnée',
      c70: 'Gravel Cycling 70K',
      c100: 'Gravel Cycling 100K',
      rr12: 'Run & Ride 12K',
      rr24: 'Run & Ride 24K',
      e12: 'Endurance 12H',
    },
    disc: {
      running: 'Gravel Running',
      cycling: 'Gravel Cycling',
      c35: 'Gravel Cycling',
      rr: 'Run & Ride',
      e12: 'Endurance',
    },
    ages: {
      eyebrow: 'Âge minimum',
      title: 'Qui peut participer ?',
      sub: 'Âge minimum en années révolues, apprécié pour chaque participant.',
      colEvent: 'Épreuve',
      colDisc: 'Discipline',
      colAge: 'Âge minimum',
      colNote: 'Précision',
      notes: {
        r5: "Sous réserve des catégories d'âge applicables",
        r10: "Sous réserve des catégories d'âge applicables",
        r20: 'Réservé aux personnes majeures',
        c35: 'Randonnée non chronométrée, sans classement',
        c70: 'Course chronométrée',
        c100: 'Course chronométrée',
        rr12: 'Pour chacun des deux équipiers · autorisation parentale pour les mineurs',
        rr24: 'Pour chacun des deux équipiers',
        e12: "Pour chaque membre de l'équipe",
      },
      minor:
        "Pour les mineurs, une autorisation parentale écrite est obligatoire, ainsi que les justificatifs de santé éventuellement applicables à l'épreuve.",
    },
    docs: {
      eyebrow: 'Documents à fournir',
      title: 'Quels justificatifs préparer ?',
      sub: "Aucun départ sans justificatif requis validé. Une pièce d'identité peut être exigée à l'entrée du camp militaire.",
      items: [
        {
          title: 'Gravel Running',
          summary: '5K, 10K et 20K · courses individuelles chronométrées',
          ref: 'Règlement, articles 3 et 4',
          open: true,
          groups: [
            {
              label: 'Participants majeurs',
              items: [
                {
                  text: 'Licence FFA admissible, selon les règles FFA applicables',
                },
                {
                  text: 'ou Pass Prévention Santé (PPS) valide :',
                  url: 'https://pps.athle.fr',
                  urlLabel: 'pps.athle.fr',
                },
              ],
              minor: false,
            },
            {
              label: 'Mineurs autorisés (5K et 10K)',
              minor: true,
              items: [
                {
                  text: 'Autorisation parentale écrite',
                },
                {
                  text: 'Questionnaire de santé et attestation, remplis avec le représentant légal',
                },
                {
                  text: 'Certificat médical si nécessaire',
                },
              ],
            },
          ],
        },
        {
          title: 'Gravel Cycling',
          summary: '35K randonnée · 70K et 100K courses chronométrées',
          ref: 'Règlement, articles 3, 4 et 8',
          groups: [
            {
              label: 'Randonnée 35K',
              items: [
                {
                  text: 'Randonnée sans classement ni chronométrage',
                },
                {
                  text: 'Mineurs de 16 ou 17 ans : autorisation parentale écrite, ainsi que tout justificatif exigé par le cadre retenu',
                },
              ],
              minor: false,
            },
            {
              label: 'Courses 70K et 100K · majeurs',
              items: [
                {
                  text: "Pour participer aux Gravel Games Cycling, il t'est indispensable de fournir à l'organisation l'un des trois documents suivants : (1) un certificat médical mentionnant la pratique du cyclisme en compétition datant de moins d'un an à la date de la fin de l'épreuve (2) une licence FFC ou UCI pour l'année en cours (3) l'attestation de suivi du module santé que tu peux compléter",
                  url: 'https://compte.ffc.fr/account/new/',
                  urlLabel: 'ICI',
                  after: 'une fois ton espace licencié créé.',
                },
              ],
              minor: false,
            },
          ],
        },
        {
          title: 'Run & Ride',
          summary: '12K et 24K · en binôme, avec un vélo partagé · cadre FFTRI',
          ref: 'Règlement, articles 3, 4 et 9',
          reserve:
            'Cadre réglementaire FFTRI. Les justificatifs sont vérifiés individuellement pour chaque équipier.',
          groups: [
            {
              label: 'Âge minimum, pour chaque équipier',
              items: [
                {
                  text: '12K : 16 ans',
                },
                {
                  text: '24K : 18 ans',
                },
                {
                  text: 'Équipier mineur (12K) : autorisation parentale obligatoire',
                },
              ],
              minor: false,
            },
            {
              label: 'Licencié·e FFTRI',
              items: [
                {
                  text: "Licence FFTRI compétition valable pour l'épreuve : présentation de la licence",
                },
              ],
              minor: false,
            },
            {
              label: 'Non licencié·e FFTRI',
              items: [
                {
                  text: "Si tu n'es pas titulaire d'une licence FFTRI compétition valable, tu devras disposer d'une Licence Expérience FFTRI, à prendre en ligne :",
                  url: 'https://espacetri.fftri.com/license-experience/event/license-experience/17404',
                  urlLabel: 'Prendre ma Licence Expérience FFTRI',
                },
                {
                  text: 'Une licence FFA ou FFC seule ne vaut pas licence FFTRI compétition',
                },
              ],
              minor: false,
            },
            {
              label: 'Formalités de santé · équipier majeur',
              items: [
                {
                  text: 'Formalités de santé correspondant à sa licence FFTRI compétition ou à sa Licence Expérience',
                },
              ],
              minor: false,
            },
            {
              label: 'Formalités de santé · équipier de 16 ou 17 ans (12K)',
              minor: true,
              items: [
                {
                  text: 'Autorisation parentale écrite',
                },
                {
                  text: 'Questionnaire de santé ou certificat médical si requis',
                },
              ],
            },
          ],
        },
        {
          title: 'Endurance 12H',
          summary: 'Équipes de 2 à 6 · participants majeurs',
          ref: 'Règlement, articles 3, 4 et 10',
          reserve:
            "Exigences de santé et de licence du format mixte en cours de validation avec les fédérations et l'assureur.",
          groups: [
            {
              label: "L'équipe",
              items: [
                {
                  text: '2 à 6 participants majeurs, inscrits nominativement',
                },
                {
                  text: 'En permanence, au moins un équipier sur la boucle Gravel Running et un autre sur la boucle Gravel Cycling',
                },
                {
                  text: 'Un repas inclus pour chaque équipier inscrit',
                },
              ],
              minor: false,
            },
            {
              label: 'Règlement particulier',
              items: [
                {
                  text: "Relais, comptage des tours, classement, pénalités et interruptions : règlement particulier envoyé à chaque équipe avant l'événement",
                },
                {
                  text: 'Il fait partie du règlement sportif et doit être accepté par chaque équipe avant le départ',
                },
              ],
              minor: false,
            },
          ],
        },
      ],
    },
    check: {
      eyebrow: 'Vérification rapide',
      title: 'Puis-je participer ?',
      sub: 'Trois questions, une réponse fondée sur le règlement sportif.',
      q1: 'Ton épreuve',
      q1ph: 'Choisis une épreuve',
      q2: "Ton âge le jour de l'épreuve",
      q2ph: 'Ex. 17',
      q3: 'Ta situation',
      lic: 'Licencié·e',
      non: 'Non licencié·e',
      reset: 'Recommencer',
      empty:
        "Réponds aux trois questions pour savoir si tu peux t'inscrire et ce que tu dois préparer.",
      okHead: "Tu peux t'inscrire",
      okAge: 'Ton âge te permet de participer à cette épreuve.',
      koHead: 'Pas possible à ton âge',
      confirmAge: 'Âge minimum en cours de confirmation : il pourrait évoluer.',
      altTitle: 'Épreuves ouvertes à ton âge',
      none: "Aucune épreuve des Gravel Games 2027 n'est ouverte avant 16 ans.",
      docsTitle: 'Justificatifs à préparer',
      minorTitle: 'Formalités pour les mineurs',
      notesTitle: 'À savoir',
      ppsLink: 'Obtenir mon PPS sur pps.athle.fr',
      licExpLink: 'Prendre ma Licence Expérience FFTRI',
      ffcHealthLink: 'Compléter le module santé sur compte.ffc.fr',
      ffcHealthUrl: 'https://compte.ffc.fr/account/new/',
      licExpUrl: 'https://espacetri.fftri.com/license-experience/event/license-experience/17404',
      pair: {
        q2: "Âge de chaque équipier le jour de l'épreuve",
        q3: 'Situation de chaque équipier',
        you: 'Toi',
        mate: 'Ton équipier',
        lic: 'Licencié·e FFTRI',
        non: 'Non licencié·e FFTRI',
        hint: 'Une licence FFA ou FFC seule ne vaut pas licence FFTRI compétition : dans ce cas, choisis « Non licencié·e FFTRI » et prends une Licence Expérience FFTRI en ligne.',
        okHead: "Ton binôme peut s'inscrire",
        koHead: 'Binôme non éligible',
        whoYou: 'toi',
        whoMate: 'ton équipier',
        whoBoth: 'les deux équipiers',
        altTitle: 'Épreuve ouverte à ton binôme',
        docsTitle: 'Justificatifs, pour chaque équipier',
        okAge: "Les deux équipiers ont l'âge requis : {{n}} ans minimum chacun.",
        koAge: 'Chaque équipier doit avoir au moins {{n}} ans révolus. Âge insuffisant : {{who}}.',
      },
      register: "Je m'inscris",
      soon: 'Inscriptions bientôt ouvertes',
      disclaimer:
        "Résultat indicatif établi d'après le règlement sportif V7. Seul le règlement intégral fait foi.",
      noDocs35:
        'Le règlement ne prévoit pas de justificatif de santé spécifique pour les majeurs sur cette randonnée non chronométrée.',
      d: {
        runLic: [
          'Ta licence FFA, si elle est admissible selon les règles FFA applicables.',
          'Sinon, un Pass Prévention Santé (PPS) valide.',
        ],
        runNon: ['Un Pass Prévention Santé (PPS) valide.'],
        runMinor: [
          'Questionnaire de santé et attestation, remplis avec ton représentant légal.',
          'Certificat médical si nécessaire.',
        ],
        c35Minor: ['Tout justificatif exigé par le cadre retenu.'],
        cyc: [
          "L'un des trois documents suivants : un certificat médical mentionnant la pratique du cyclisme en compétition, datant de moins d'un an à la date de la fin de l'épreuve ;",
          "ou une licence FFC ou UCI pour l'année en cours ;",
          "ou l'attestation de suivi du module santé, à compléter une fois ton espace licencié créé.",
        ],
        rrLic: ["Licence FFTRI compétition valable pour l'épreuve, à présenter."],
        rrNon: ['Une Licence Expérience FFTRI, à prendre en ligne.'],
        rrHealthAdult: 'Formalités de santé correspondant à la licence ou à la Licence Expérience.',
        e12: [
          "Exigences de santé et de licence spécifiques au format mixte, en cours de validation avec les fédérations et l'assureur.",
        ],
      },
      m: {
        consent: 'Autorisation parentale écrite.',
        rrHealth: 'Questionnaire de santé ou certificat médical si requis.',
      },
      n: {
        ppsNote: 'Le PPS FFA ne remplace pas automatiquement les justificatifs cyclistes.',
        noEbike: 'Vélo sans assistance électrique sur les courses chronométrées.',
        untimed: 'Randonnée non chronométrée et sans classement.',
        rrFrame:
          'Cadre réglementaire FFTRI : justificatifs vérifiés individuellement pour chaque équipier.',
        rrBike: 'Un seul vélo sans assistance électrique par binôme ; arrivée ensemble.',
        e12Team: 'Équipe de 2 à 6 participants majeurs, inscrits nominativement.',
        e12Rules: "Règlement particulier envoyé avant l'événement, à accepter par chaque équipe.",
        noStart: 'Aucun départ sans justificatif requis validé.',
        id: "Une pièce d'identité peut être exigée à l'entrée du camp militaire.",
      },
      koAge: 'Cette épreuve est réservée aux participants de {{n}} ans révolus et plus.',
    },
    rules: {
      eyebrow: 'Règles essentielles',
      title: "Un terrain d'exception, des règles claires",
      sub: 'Les Gravel Games se déroulent dans un camp militaire habituellement fermé au public. Respecte strictement les zones autorisées et les consignes de sécurité : les consignes des autorités militaires priment en cas de nécessité.',
      items: [
        {
          title: 'Sécurité et équipements obligatoires',
          ref: 'Articles 7, 8 et 13',
          text: "Running : dossard visible à l'avant et chaussures adaptées. Cycling : casque homologué attaché, vélo en bon état, sans assistance électrique sur les courses chronométrées. Chacun porte assistance à une personne en danger et alerte les secours.",
        },
        {
          title: 'Parcours et points de contrôle',
          ref: 'Articles 6 et 15',
          text: 'Suis le balisage, les points de contrôle et les signaleurs. Aucun raccourci ni assistance motorisée non autorisée. Une sortie de parcours ou un contrôle manqué peut entraîner une pénalité, voire une disqualification.',
        },
        {
          title: 'Binômes et équipes',
          ref: 'Articles 9 et 10',
          text: 'Run & Ride : un vélo partagé, passé de la main à la main sans gain de terrain, et une arrivée ensemble. Endurance 12H : en permanence un équipier en running et un autre en cycling, selon le règlement particulier.',
        },
        {
          title: 'Barrières horaires et abandons',
          ref: 'Articles 12 et 13',
          text: 'Départs et barrières horaires sont prévisionnels et précisés dans le guide participant. Le médecin, les secouristes ou la direction de course peuvent arrêter un participant pour raison de santé ou de sécurité.',
          dark: true,
        },
        {
          title: 'Camp militaire et environnement',
          ref: 'Articles 5 et 14',
          text: "Accès réglementé : suis les procédures d'identification et de contrôle. Interdiction d'entrer dans les zones fermées, de photographier les installations sensibles ou de faire voler un drone sans autorisation. Aucun déchet hors des zones de collecte.",
        },
        {
          title: 'Modifications possibles',
          ref: 'Articles 1 et 12',
          text: "Distances, parcours, horaires et accès au camp sont prévisionnels. Pour raison de sécurité, de météo ou de contraintes militaires, l'organisation peut les adapter, ou neutraliser, reporter, interrompre ou annuler une épreuve.",
        },
      ],
    },
    ins: {
      eyebrow: 'Assurances et responsabilité',
      title: 'Bien couvert le jour J',
      sub: "Ce que prévoit l'organisation, et ce qu'il t'appartient de vérifier.",
      items: [
        {
          title: 'Responsabilité civile',
          tag: "Prévue par l'organisation",
          kind: 'org',
          text: "L'association Wish One Racing, organisatrice des Gravel Games, met en place une couverture responsabilité civile, dans les conditions et limites de son contrat d'assurance. Elle couvre sa responsabilité, celle de ses préposés et bénévoles et, dans les limites prévues au contrat, la responsabilité civile pouvant incomber aux participants pendant l'événement. Elle intervient en complément ou, à défaut, de tes assurances personnelles. Une attestation d'assurance pourra être communiquée sur simple demande.",
        },
        {
          title: 'Assurance individuelle accident',
          tag: 'Vivement recommandée',
          kind: 'rec',
          text: "Elle couvre tes propres dommages. Vérifie que tes garanties personnelles sont adaptées à la pratique sportive, y compris en compétition lorsque cela s'applique. Si ta couverture est insuffisante, souscris une assurance complémentaire couvrant notamment :",
          points: [
            'Les dommages corporels',
            'Les frais médicaux',
            'Les frais de recherche et de secours',
            "L'assistance et le rapatriement",
            'Les dommages au matériel',
          ],
          foot: "L'organisation pourra proposer, lors de l'inscription, une solution d'assurance partenaire.",
        },
        {
          title: 'Dommages matériels',
          tag: 'À ta charge',
          kind: 'you',
          text: "Tu restes responsable de la surveillance de ton vélo, de tes équipements et de tes effets personnels. Les éventuelles limitations de responsabilité de l'organisateur s'appliquent uniquement dans les limites permises par la loi.",
        },
      ],
      reserve: '',
      link: "Lire l'article 16 du règlement",
    },
    cancel: {
      eyebrow: "Annulation d'inscription",
      title: "Un imprévu ? Pense à l'assurance annulation.",
      status: "Assurance annulation facultative, proposée au moment de l'inscription.",
      keyTitle: "L'essentiel",
      faqTitle: 'Questions fréquentes',
      points: [
        "Une assurance annulation facultative est proposée au moment de l'inscription, par la plateforme d'inscription et son partenaire assureur.",
        'Elle doit être souscrite avant la validation définitive de la commande.',
        "Elle permet, dans les cas et limites prévus par le contrat d'assurance, de demander le remboursement des droits d'inscription versés.",
        "Sans cette assurance, un désistement volontaire ne donne en principe lieu à aucun remboursement par l'organisation, sous réserve des dispositions légales impératives et des conditions générales d'inscription.",
        "Binômes Run & Ride et équipes Endurance 12H : la prise en charge d'un désistement individuel, d'un remplacement ou d'un remboursement dépend des conditions d'inscription et du contrat d'assurance annulation.",
      ],
      faq: [
        {
          q: 'Puis-je annuler mon inscription ?',
          a: "Oui, mais un éventuel remboursement dépend notamment de l'assurance annulation, si tu l'as souscrite, et des conditions générales d'inscription communiquées avant le paiement.",
        },
        {
          q: "L'assurance annulation est-elle obligatoire ?",
          a: 'Non, elle est facultative.',
        },
        {
          q: 'Puis-je la souscrire après mon inscription ?',
          a: 'Non. Elle doit être souscrite avant la validation définitive de ta commande.',
        },
        {
          q: 'Et si mon binôme ou un équipier se désiste ?',
          a: "La prise en charge d'un désistement individuel, le remplacement d'un membre ou un remboursement dépendent des conditions d'inscription et du contrat d'assurance annulation. Toute substitution exige l'accord de l'organisateur et la validation des justificatifs. Aucune couverture automatique de l'ensemble du binôme ou de l'équipe n'est présumée.",
        },
        {
          q: "Et si l'organisation annule ou reporte une épreuve ?",
          a: "C'est une situation distincte du désistement volontaire. Elle est régie par les conditions générales d'inscription et les dispositions légales applicables. Le règlement ne limite pas tes droits légaux.",
        },
      ],
      link: "Lire l'article 17 du règlement",
    },
    full: {
      eyebrow: 'Règlement sportif',
      title: 'Les règlements complets',
      sub: "Cette rubrique ne remplace pas le règlement intégral. En cas de divergence d'interprétation, la version française prévaut.",
      readOnline: 'Lire en ligne',
      download: 'PDF',
      docs: [
        {
          title: 'Règlement sportif 2027',
          lang: 'Version française',
          code: 'fr',
          file: 'Reglement_Gravel_Games_2027_FR_V7.pdf',
          pdf: '/site/reglement-sportif-fr.pdf',
        },
        {
          title: 'Sporting Rules 2027',
          lang: 'Version anglaise',
          code: 'en',
          file: 'Gravel_Games_2027_Reglement_Gravel_Games_2027_EN_V7.pdf',
          pdf: '/site/sporting-rules-en.pdf',
        },
      ],
      version:
        "Version V7 · édition 2027, applicable à compter de sa publication officielle par l'organisation.",
    },
    docTitle: 'Conditions de participation · Gravel Games',
    years: '{{n}} ans',
  },
  en: {
    eyebrow: 'Entry Requirements',
    title: 'Everything you need to know before the start',
    intro:
      'Gravel Running, Gravel Cycling, Run & Ride or 12H Endurance: each format has its own entry requirements. Find the essentials here to choose your event, prepare your entry and line up in the best conditions.',
    jumpLabel: 'In this section',
    jump: [
      {
        label: 'Minimum age',
        href: '#ages',
      },
      {
        label: 'Documents',
        href: '#documents',
      },
      {
        label: 'Can I take part?',
        href: '#verifier',
      },
      {
        label: 'Key rules',
        href: '#regles',
      },
      {
        label: 'Insurance',
        href: '#assurances',
      },
      {
        label: 'Cancellation',
        href: '#annulation',
      },
      {
        label: 'Full rules',
        href: '#reglement',
      },
    ],
    draft:
      'Content based on the Sporting Rules V7 for the 2027 edition. This simplified overview does not replace the full rules, which prevail.',
    readRules: 'Read the full rules',
    readDetail: 'See details',
    seeEvent: 'See the event',
    ev: {
      r5: 'Gravel Running 5K',
      r10: 'Gravel Running 10K',
      r20: 'Gravel Running 20K',
      c35: 'Gravel Cycling 35K — Ride',
      c70: 'Gravel Cycling 70K',
      c100: 'Gravel Cycling 100K',
      rr12: 'Run & Ride 12K',
      rr24: 'Run & Ride 24K',
      e12: '12H Endurance',
    },
    disc: {
      running: 'Gravel Running',
      cycling: 'Gravel Cycling',
      c35: 'Gravel Cycling',
      rr: 'Run & Ride',
      e12: 'Endurance',
    },
    ages: {
      eyebrow: 'Minimum age',
      title: 'Who can take part?',
      sub: 'Minimum age in full years, applied to each participant.',
      colEvent: 'Event',
      colDisc: 'Discipline',
      colAge: 'Minimum age',
      colNote: 'Details',
      notes: {
        r5: 'Subject to applicable federation age categories',
        r10: 'Subject to applicable federation age categories',
        r20: 'Adults only',
        c35: 'Untimed, unranked recreational ride',
        c70: 'Timed race',
        c100: 'Timed race',
        rr12: 'For each of the two team members · parental consent for minors',
        rr24: 'For each of the two team members',
        e12: 'For every team member',
      },
      minor:
        'Minors must provide written parent/guardian consent, along with any health documentation applicable to their event.',
    },
    docs: {
      eyebrow: 'Documents',
      title: 'Which documents do I need?',
      sub: 'No start without the required approved documents. Photo ID may be required at the entrance to the military camp.',
      items: [
        {
          title: 'Gravel Running',
          summary: '5K, 10K and 20K · timed individual races',
          ref: 'Rules, sections 3 and 4',
          open: true,
          groups: [
            {
              label: 'Adults',
              items: [
                {
                  text: 'An accepted FFA licence, under the applicable FFA rules',
                },
                {
                  text: 'or a valid Pass Prévention Santé (PPS):',
                  url: 'https://pps.athle.fr',
                  urlLabel: 'pps.athle.fr',
                },
              ],
              minor: false,
            },
            {
              label: 'Eligible minors (5K and 10K)',
              minor: true,
              items: [
                {
                  text: 'Written parent/guardian consent',
                },
                {
                  text: 'Health questionnaire and parent/guardian attestation',
                },
                {
                  text: 'Medical certificate where required',
                },
              ],
            },
          ],
        },
        {
          title: 'Gravel Cycling',
          summary: '35K ride · 70K and 100K timed races',
          ref: 'Rules, sections 3, 4 and 8',
          groups: [
            {
              label: '35K ride',
              items: [
                {
                  text: 'Untimed, unranked recreational ride',
                },
                {
                  text: 'Riders aged 16 or 17: written parent/guardian consent and any other documents required under the chosen framework',
                },
              ],
              minor: false,
            },
            {
              label: '70K and 100K races · adults',
              items: [
                {
                  text: 'To take part in the Gravel Games Cycling, you must provide the organisers with one of the following three documents: (1) a medical certificate stating fitness for competitive cycling, dated less than one year before the end of the event (2) an FFC or UCI licence for the current year (3) the certificate of completion of the health module, which you can complete',
                  url: 'https://compte.ffc.fr/account/new/',
                  urlLabel: 'HERE',
                  after: 'once your licence-holder account has been created.',
                },
              ],
              minor: false,
            },
          ],
        },
        {
          title: 'Run & Ride',
          summary: '12K and 24K · in pairs, with one shared bike · FFTRI framework',
          ref: 'Rules, sections 3, 4 and 9',
          reserve:
            'FFTRI regulatory framework. Documents are checked individually for each team member.',
          groups: [
            {
              label: 'Minimum age, for each team member',
              items: [
                {
                  text: '12K: 16',
                },
                {
                  text: '24K: 18',
                },
                {
                  text: 'Team member under 18 (12K): parental consent required',
                },
              ],
              minor: false,
            },
            {
              label: 'FFTRI licence holders',
              items: [
                {
                  text: 'A valid FFTRI competition licence accepted for the event: show your licence',
                },
              ],
              minor: false,
            },
            {
              label: 'Without an FFTRI licence',
              items: [
                {
                  text: 'If you do not hold a valid FFTRI competition licence, you will need an FFTRI Licence Expérience, available online:',
                  url: 'https://espacetri.fftri.com/license-experience/event/license-experience/17404',
                  urlLabel: 'Get my FFTRI Licence Expérience',
                },
                {
                  text: 'An FFA or FFC licence alone is not equivalent to an FFTRI competition licence',
                },
              ],
              minor: false,
            },
            {
              label: 'Health formalities · adult team member',
              items: [
                {
                  text: 'The health formalities corresponding to their FFTRI competition licence or Licence Expérience',
                },
              ],
              minor: false,
            },
            {
              label: 'Health formalities · team member aged 16 or 17 (12K)',
              minor: true,
              items: [
                {
                  text: 'Written parent/guardian consent',
                },
                {
                  text: 'Health questionnaire or medical certificate where required',
                },
              ],
            },
          ],
        },
        {
          title: '12H Endurance',
          summary: 'Teams of 2 to 6 · adults only',
          ref: 'Rules, sections 3, 4 and 10',
          reserve:
            'Health and licensing arrangements for this mixed-discipline format are still to be confirmed with the federations and insurer.',
          groups: [
            {
              label: 'The team',
              items: [
                {
                  text: '2 to 6 adult entrants, registered by name',
                },
                {
                  text: 'At all times, at least one team member on the Gravel Running loop and another on the Gravel Cycling loop',
                },
                {
                  text: 'One meal included for each registered team member',
                },
              ],
              minor: false,
            },
            {
              label: 'Event-specific rules',
              items: [
                {
                  text: 'Relays, lap counting, rankings, penalties and interruptions: event-specific rules sent to every team before the event',
                },
                {
                  text: 'They form part of the sporting rules and must be accepted by each team before the start',
                },
              ],
              minor: false,
            },
          ],
        },
      ],
    },
    check: {
      eyebrow: 'Quick check',
      title: 'Can I take part?',
      sub: 'Three questions, one answer based on the sporting rules.',
      q1: 'Your event',
      q1ph: 'Choose an event',
      q2: 'Your age on race day',
      q2ph: 'e.g. 17',
      q3: 'Your status',
      lic: 'Licence holder',
      non: 'No licence',
      reset: 'Start again',
      empty:
        'Answer the three questions to see whether you can enter and what you need to prepare.',
      okHead: 'You can enter',
      okAge: 'Your age allows you to take part in this event.',
      koHead: 'Not possible at your age',
      confirmAge: 'Minimum age still to be confirmed: it may change.',
      altTitle: 'Events open at your age',
      none: 'No Gravel Games 2027 event is open to under-16s.',
      docsTitle: 'Documents to prepare',
      minorTitle: 'Requirements for minors',
      notesTitle: 'Good to know',
      ppsLink: 'Get my PPS on pps.athle.fr',
      licExpLink: 'Get my FFTRI Licence Expérience',
      ffcHealthLink: 'Complete the health module on compte.ffc.fr',
      ffcHealthUrl: 'https://compte.ffc.fr/account/new/',
      licExpUrl: 'https://espacetri.fftri.com/license-experience/event/license-experience/17404',
      pair: {
        q2: "Each team member's age on race day",
        q3: "Each team member's status",
        you: 'You',
        mate: 'Your partner',
        lic: 'FFTRI licence holder',
        non: 'No FFTRI licence',
        hint: 'An FFA or FFC licence alone is not equivalent to an FFTRI competition licence: in that case, choose “No FFTRI licence” and get an FFTRI Licence Expérience online.',
        okHead: 'Your pair can enter',
        koHead: 'Pair not eligible',
        whoYou: 'you',
        whoMate: 'your partner',
        whoBoth: 'both team members',
        altTitle: 'Event open to your pair',
        docsTitle: 'Documents, for each team member',
        okAge: 'Both team members meet the minimum age of {{n}}.',
        koAge: 'Each team member must be at least {{n}}. Under the minimum age: {{who}}.',
      },
      register: 'Enter now',
      soon: 'Entries opening soon',
      disclaimer:
        'Indicative answer based on the Sporting Rules V7. Only the full rules are binding.',
      noDocs35:
        'The rules do not set out specific health documents for adults on this untimed recreational ride.',
      d: {
        runLic: [
          'Your FFA licence, if it is accepted under the applicable FFA rules.',
          'Otherwise, a valid Pass Prévention Santé (PPS).',
        ],
        runNon: ['A valid Pass Prévention Santé (PPS).'],
        runMinor: [
          'Health questionnaire and parent/guardian attestation.',
          'Medical certificate where required.',
        ],
        c35Minor: ['Any other documents required under the chosen framework.'],
        cyc: [
          'One of the following three documents: a medical certificate stating fitness for competitive cycling, dated less than one year before the end of the event;',
          'or an FFC or UCI licence for the current year;',
          'or the certificate of completion of the health module, once your licence-holder account has been created.',
        ],
        rrLic: ['A valid FFTRI competition licence accepted for the event, to be shown.'],
        rrNon: ['An FFTRI Licence Expérience, available online.'],
        rrHealthAdult: 'The health formalities corresponding to the licence or Licence Expérience.',
        e12: [
          'Health and licensing arrangements for this mixed-discipline format are still to be confirmed with the federations and insurer.',
        ],
      },
      m: {
        consent: 'Written parent/guardian consent.',
        rrHealth: 'Health questionnaire or medical certificate where required.',
      },
      n: {
        ppsNote: 'An FFA PPS does not automatically replace cycling documents.',
        noEbike: 'No electric assistance in timed races.',
        untimed: 'Untimed, unranked recreational ride.',
        rrFrame: 'FFTRI framework: documents are checked individually for each team member.',
        rrBike: 'One shared non-electric bike per pair; both partners finish together.',
        e12Team: 'Teams of 2 to 6 adult entrants, registered by name.',
        e12Rules: 'Event-specific rules sent before the event, to be accepted by each team.',
        noStart: 'No start without the required approved documents.',
        id: 'Photo ID may be required at the entrance to the military camp.',
      },
      koAge: 'This event is open to participants aged {{n}} and over.',
    },
    rules: {
      eyebrow: 'Key rules',
      title: 'An exceptional venue, clear rules',
      sub: 'The Gravel Games take place on a military camp that is normally closed to the public. Stay strictly within authorised areas and follow all safety instructions: military instructions take priority where required.',
      items: [
        {
          title: 'Safety and compulsory equipment',
          ref: 'Sections 7, 8 and 13',
          text: 'Running: bib clearly visible on the front and suitable off-road shoes. Cycling: fastened certified helmet, roadworthy bike, no electric assistance in timed races. Everyone must help anyone in danger and alert emergency staff.',
        },
        {
          title: 'Routes and checkpoints',
          ref: 'Sections 6 and 15',
          text: 'Follow official markings, checkpoints and marshals. No shortcuts or unauthorised motorised support. Leaving the course or missing a checkpoint may lead to a penalty or disqualification.',
        },
        {
          title: 'Pairs and teams',
          ref: 'Sections 9 and 10',
          text: 'Run & Ride: one shared bike, handed over directly without gaining ground, and both partners finish together. 12H Endurance: one runner and one cyclist active at all times, under the event-specific rules.',
        },
        {
          title: 'Cut-offs and withdrawals',
          ref: 'Sections 12 and 13',
          text: 'Start times and cut-offs are provisional and set out in the participant guide. The event doctor, first aid staff or race director may stop a participant for health or safety reasons.',
          dark: true,
        },
        {
          title: 'Military camp and environment',
          ref: 'Sections 5 and 14',
          text: 'Restricted site: follow identity and security checks. No entry to closed areas, no photography of designated sensitive facilities and no drone flights without express permission. Litter only in designated areas.',
        },
        {
          title: 'Possible changes',
          ref: 'Sections 1 and 12',
          text: 'Distances, routes, start times and camp access are provisional. For safety, weather or military reasons, the organiser may adjust them, or neutralise, postpone, stop or cancel an event.',
        },
      ],
    },
    ins: {
      eyebrow: 'Insurance and liability',
      title: 'Covered on race day',
      sub: 'What the organiser arranges, and what you should check yourself.',
      items: [
        {
          title: 'Public liability insurance',
          tag: 'Arranged by the organiser',
          kind: 'org',
          text: "Wish One Racing, the organiser of the Gravel Games, arranges public liability insurance within the terms and limits of its policy. It covers the organiser's liability and that of its staff and volunteers and, within the policy limits, any civil liability of participants covered during the event. It operates alongside, or where appropriate in the absence of, your own insurance. An insurance certificate may be provided upon request.",
        },
        {
          title: 'Personal accident insurance',
          tag: 'Strongly recommended',
          kind: 'rec',
          text: 'This covers your own injuries and losses. Check that your cover is appropriate for the sporting activities involved, including competition where applicable. If your cover is not sufficient, arrange additional insurance, in particular for:',
          points: [
            'Bodily injury',
            'Medical expenses',
            'Search and rescue costs',
            'Assistance and repatriation',
            'Equipment damage',
          ],
          foot: 'The organiser may offer partner insurance during registration.',
        },
        {
          title: 'Personal property',
          tag: 'Your responsibility',
          kind: 'you',
          text: "You remain responsible for supervising your bike, sporting equipment and personal belongings. Any limitation of the organiser's liability applies only to the extent permitted by law.",
        },
      ],
      reserve: '',
      link: 'Read section 16 of the rules',
    },
    cancel: {
      eyebrow: 'Cancelling an entry',
      title: 'Plans may change: consider cancellation insurance.',
      status: 'Optional cancellation insurance, offered during registration.',
      keyTitle: 'Key points',
      faqTitle: 'Frequently asked questions',
      points: [
        'Optional cancellation insurance is offered during registration, through the registration platform and its insurance partner.',
        'It must be purchased before your order is finally confirmed.',
        'Subject to the circumstances, exclusions and limits of the policy, it allows you to seek reimbursement of the entry fees paid.',
        'Without it, a voluntary withdrawal will generally not entitle you to a refund from the organiser, subject to mandatory applicable law and the entry terms.',
        "Run & Ride pairs and 12H Endurance teams: one member's withdrawal, substitution or any refund depends on the entry terms and the cancellation insurance policy.",
      ],
      faq: [
        {
          q: 'Can I cancel my entry?',
          a: 'Yes, but any refund depends in particular on the cancellation insurance, if you purchased it, and on the entry terms disclosed before payment.',
        },
        {
          q: 'Is cancellation insurance compulsory?',
          a: 'No, it is optional.',
        },
        {
          q: 'Can I buy it after registering?',
          a: 'No. It must be purchased before your order is finally confirmed.',
        },
        {
          q: 'What if my partner or a team member withdraws?',
          a: "One member's withdrawal, substitution or any refund depends on the entry terms and the cancellation insurance policy. Substitutions require organiser approval and document checks. Cover for the entire pair or team is not automatically assumed.",
        },
        {
          q: 'What if the organiser cancels or postpones an event?',
          a: 'This is separate from a voluntary withdrawal. It is governed by the entry terms and applicable law. Nothing in the rules limits your statutory rights.',
        },
      ],
      link: 'Read section 17 of the rules',
    },
    full: {
      eyebrow: 'Sporting rules',
      title: 'The full rules',
      sub: 'This section does not replace the full rules. In case of differences in interpretation, the French version prevails.',
      readOnline: 'Read online',
      download: 'PDF',
      docs: [
        {
          title: 'Sporting Rules 2027',
          lang: 'English version',
          code: 'en',
          file: 'Gravel_Games_2027_Reglement_Gravel_Games_2027_EN_V7.pdf',
          pdf: '/site/sporting-rules-en.pdf',
        },
        {
          title: 'Règlement sportif 2027',
          lang: 'French version (prevails)',
          code: 'fr',
          file: 'Reglement_Gravel_Games_2027_FR_V7.pdf',
          pdf: '/site/reglement-sportif-fr.pdf',
        },
      ],
      version:
        'Version V7 · 2027 edition, applicable from its official publication by the organiser.',
    },
    docTitle: 'Entry requirements · Gravel Games',
    years: '{{n}}+',
  },
};
